import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Palette, Moon, Sun, Star, Cloud, Heart, Trees, Waves } from 'lucide-react';

const themes = [
  {
    id: 'default',
    name: 'Standard',
    icon: Palette,
    bgClass: 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800',
    preview: 'bg-gradient-to-br from-blue-50 to-indigo-100'
  },
  {
    id: 'moon-stars',
    name: 'Mond & Sterne',
    icon: Moon,
    bgClass: 'bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900',
    preview: 'bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900',
    hasStars: true
  },
  {
    id: 'sunset',
    name: 'Sonnenuntergang',
    icon: Sun,
    bgClass: 'bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600',
    preview: 'bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600'
  },
  {
    id: 'cloudy',
    name: 'Wolken',
    icon: Cloud,
    bgClass: 'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500',
    preview: 'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500'
  },
  {
    id: 'hearts',
    name: 'Herzen',
    icon: Heart,
    bgClass: 'bg-gradient-to-br from-pink-200 via-red-200 to-pink-300',
    preview: 'bg-gradient-to-br from-pink-200 via-red-200 to-pink-300'
  },
  {
    id: 'forest',
    name: 'Wald',
    icon: Trees,
    bgClass: 'bg-gradient-to-br from-emerald-800 via-green-700 to-emerald-600',
    preview: 'bg-gradient-to-br from-emerald-800 via-green-700 to-emerald-600'
  },
  {
    id: 'ocean',
    name: 'Ozean',
    icon: Waves,
    bgClass: 'bg-gradient-to-br from-blue-900 via-blue-700 to-cyan-600',
    preview: 'bg-gradient-to-br from-blue-900 via-blue-700 to-cyan-600'
  }
];

export const ThemeSelector = ({ selectedTheme, onThemeChange }) => {
  const [showSelector, setShowSelector] = useState(false);

  const handleThemeSelect = (themeId) => {
    onThemeChange(themeId);
    setShowSelector(false);
  };

  const currentTheme = themes.find(theme => theme.id === selectedTheme) || themes[0];

  return (
    <div className="relative">
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200"
      >
        <currentTheme.icon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {currentTheme.name}
        </span>
      </button>

      {showSelector && createPortal(
        <>
          <div 
            className="fixed inset-0 z-[99998]" 
            onClick={() => setShowSelector(false)}
          />
          <div className="fixed left-1/2 top-20 transform -translate-x-1/2 w-72 sm:w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[99999]">
            <div className="p-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Hintergrund auswählen
              </h3>
              <div className="space-y-2">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeSelect(theme.id)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200
                      ${selectedTheme === theme.id
                        ? 'bg-primary-50 dark:bg-blue-900 border border-primary-200 dark:border-blue-800 dark:text-white'
                        : 'hover:bg-gray-50 dark:hover:bg-blue-800 dark:bg-gray-800 dark:text-white'}
                    `}
                  >
                    <div className={`w-8 h-8 rounded-md ${theme.preview} flex items-center justify-center`}>
                      {theme.hasStars && (
                        <div className="relative w-full h-full">
                          <div className="absolute top-1 left-1 w-1 h-1 bg-white rounded-full opacity-80"></div>
                          <div className="absolute top-2 right-2 w-0.5 h-0.5 bg-white rounded-full opacity-60"></div>
                          <div className="absolute bottom-1 left-2 w-0.5 h-0.5 bg-white rounded-full opacity-70"></div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 flex-1">
                      <theme.icon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      <span className="text-sm font-medium text-gray-700 dark:text-white">
                        {theme.name}
                      </span>
                    </div>
                    {selectedTheme === theme.id && (
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}; 